/**
 * WerkstattAuftraegePage — Kanban-Board für Werkstatt-Aufträge
 * Route: /werkstatt/auftraege
 *
 * Features (Issue #392):
 *   • Kanban-Spalten für aktive Status-Kette (angenommen → fertig)
 *   • "Abgeschlossen"-Toggle für abgeholt, rechnung_erstellt, bezahlt
 *   • Drag & Drop zwischen Spalten via @dnd-kit/core
 *     — unzulässige Übergänge werden per Toast abgelehnt
 *   • Multi-Select Mechaniker-Filter
 *   • Karten: Akten-Nr, Kunde, Fahrzeug, Mechaniker-Avatar, voraussichtlich_fertig
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'wouter';
import { useAuth } from '@/contexts/AuthContext';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  ArrowLeft,
  Calendar,
  Car,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  User,
  Wrench,
} from 'lucide-react';
import { toast } from 'sonner';
// Spiegelt lib/werkstatt-status.ts (Single source of truth liegt im Backend).
// Nur die für die UI benötigten Teile (keine Supabase-Abhängigkeit).
const WERKSTATT_STATUS = [
  'angenommen', 'in_werkstatt', 'in_arbeit', 'warten_auf_teile',
  'fertig', 'abgeholt', 'rechnung_erstellt', 'bezahlt',
] as const;
type WerkstattStatus = (typeof WERKSTATT_STATUS)[number];

const ALLOWED_TRANSITIONS: Record<string, readonly WerkstattStatus[]> = {
  __initial__: ['angenommen'],
  angenommen: ['in_werkstatt'],
  in_werkstatt: ['in_arbeit'],
  in_arbeit: ['warten_auf_teile', 'fertig'],
  warten_auf_teile: ['in_arbeit', 'fertig'],
  fertig: ['abgeholt'],
  abgeholt: ['rechnung_erstellt'],
  rechnung_erstellt: ['bezahlt'],
  bezahlt: [],
};

function isAllowedTransition(from: WerkstattStatus | null, to: WerkstattStatus): boolean {
  const key = from ?? '__initial__';
  return (ALLOWED_TRANSITIONS[key] ?? []).includes(to);
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface Mechaniker {
  id: string;
  vorname: string;
  nachname: string;
  kuerzel: string;
  farbe: string | null;
  avatar_url: string | null;
}

interface WerkstattAuftrag {
  id: string;
  akten_nummer: string | null;
  business_unit: string;
  kunde_name: string | null;
  kennzeichen: string | null;
  werkstatt_status: WerkstattStatus | null;
  mechaniker_id: string | null;
  voraussichtlich_fertig: string | null;
  geloescht: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const AKTIVE_SPALTEN: WerkstattStatus[] = [
  'angenommen',
  'in_werkstatt',
  'in_arbeit',
  'warten_auf_teile',
  'fertig',
];

const ABGESCHLOSSEN_SPALTEN: WerkstattStatus[] = [
  'abgeholt',
  'rechnung_erstellt',
  'bezahlt',
];

const STATUS_LABEL: Record<WerkstattStatus, string> = {
  angenommen: 'Angenommen',
  in_werkstatt: 'In Werkstatt',
  in_arbeit: 'In Arbeit',
  warten_auf_teile: 'Warten auf Teile',
  fertig: 'Fertig',
  abgeholt: 'Abgeholt',
  rechnung_erstellt: 'Rechnung erstellt',
  bezahlt: 'Bezahlt',
};

const STATUS_FARBE: Record<WerkstattStatus, string> = {
  angenommen: 'bg-gray-100 border-gray-200',
  in_werkstatt: 'bg-blue-50 border-blue-200',
  in_arbeit: 'bg-yellow-50 border-yellow-200',
  warten_auf_teile: 'bg-orange-50 border-orange-200',
  fertig: 'bg-green-50 border-green-200',
  abgeholt: 'bg-emerald-50 border-emerald-200',
  rechnung_erstellt: 'bg-purple-50 border-purple-200',
  bezahlt: 'bg-teal-50 border-teal-200',
};

const STATUS_HEADER_FARBE: Record<WerkstattStatus, string> = {
  angenommen: 'bg-gray-500',
  in_werkstatt: 'bg-blue-500',
  in_arbeit: 'bg-yellow-500',
  warten_auf_teile: 'bg-orange-500',
  fertig: 'bg-green-500',
  abgeholt: 'bg-emerald-500',
  rechnung_erstellt: 'bg-purple-500',
  bezahlt: 'bg-teal-500',
};

// ─── Data hooks ───────────────────────────────────────────────────────────────

function useAuftraege() {
  const { session } = useAuth();
  const [auftraege, setAuftraege] = useState<WerkstattAuftrag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = session?.access_token;
      const res = await fetch('/api/werkstatt-kanban?action=list', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setAuftraege(json.auftraege ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  useEffect(() => { load(); }, [load]);

  return { auftraege, setAuftraege, loading, error, reload: load };
}

function useMechaniker() {
  const { session } = useAuth();
  const [mechaniker, setMechaniker] = useState<Mechaniker[]>([]);

  useEffect(() => {
    const token = session?.access_token;
    fetch('/api/werkstatt-kanban?action=mechaniker', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.json())
      .then((j) => setMechaniker(j.mechaniker ?? []))
      .catch(() => {});
  }, [session?.access_token]);

  return mechaniker;
}

// ─── Avatar helper ────────────────────────────────────────────────────────────

function MechanikerAvatar({ mech, size = 'sm' }: { mech: Mechaniker; size?: 'sm' | 'xs' }) {
  const dim = size === 'xs' ? 'w-5 h-5 text-[10px]' : 'w-6 h-6 text-xs';
  return (
    <span
      className={`${dim} rounded-full flex items-center justify-center text-white font-semibold shrink-0`}
      style={{ backgroundColor: mech.farbe ?? '#6B7280' }}
      title={`${mech.vorname} ${mech.nachname}`}
    >
      {mech.kuerzel.slice(0, 2)}
    </span>
  );
}

// ─── Mechanic filter ──────────────────────────────────────────────────────────

interface MechanikerFilterProps {
  mechaniker: Mechaniker[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}

function MechanikerFilter({ mechaniker, selected, onChange }: MechanikerFilterProps) {
  const [open, setOpen] = useState(false);
  const allSelected = selected.size === 0;

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onChange(next);
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:border-gray-300 transition-colors"
      >
        <User className="w-3.5 h-3.5 text-gray-400" />
        {allSelected ? 'Alle Mechaniker' : `${selected.size} Mechaniker`}
        <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
      </button>

      {open && (
        <div className="absolute top-full mt-1 left-0 z-20 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[200px] py-1">
          <button
            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
            onClick={() => { onChange(new Set()); setOpen(false); }}
          >
            <span className={`w-4 h-4 rounded border flex items-center justify-center ${allSelected ? 'bg-[#B91C1C] border-[#B91C1C]' : 'border-gray-300'}`}>
              {allSelected && <CheckCircle2 className="w-3 h-3 text-white" />}
            </span>
            Alle
          </button>
          {mechaniker.map((m) => {
            const checked = selected.has(m.id);
            return (
              <button
                key={m.id}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                onClick={() => toggle(m.id)}
              >
                <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${checked ? 'border-transparent' : 'border-gray-300'}`}
                  style={checked ? { backgroundColor: m.farbe ?? '#B91C1C' } : undefined}>
                  {checked && <CheckCircle2 className="w-3 h-3 text-white" />}
                </span>
                <MechanikerAvatar mech={m} size="xs" />
                <span className="truncate">{m.vorname} {m.nachname}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Kanban Card ──────────────────────────────────────────────────────────────

interface KanbanCardProps {
  auftrag: WerkstattAuftrag;
  mechaniker: Mechaniker[];
  isDragOverlay?: boolean;
}

function KanbanCard({ auftrag, mechaniker, isDragOverlay = false }: KanbanCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: auftrag.id,
    data: { auftrag },
  });

  const mech = mechaniker.find((m) => m.id === auftrag.mechaniker_id);
  const fertigDate = auftrag.voraussichtlich_fertig
    ? new Date(auftrag.voraussichtlich_fertig).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
      })
    : null;

  const style = transform && !isDragOverlay
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  return (
    <div
      ref={isDragOverlay ? undefined : setNodeRef}
      style={style}
      {...(isDragOverlay ? {} : { ...attributes, ...listeners })}
      data-auftrag-id={auftrag.id}
      className={`bg-white rounded-lg border border-gray-200 p-3 cursor-grab active:cursor-grabbing shadow-sm
        hover:shadow-md hover:border-gray-300 transition-all select-none
        ${isDragging && !isDragOverlay ? 'opacity-30' : ''}
        ${isDragOverlay ? 'shadow-lg rotate-1 cursor-grabbing' : ''}`}
    >
      <Link
        href={`/werkstatt/auftraege/${auftrag.id}`}
        onClick={(e) => e.stopPropagation()}
        className="block"
      >
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <span className="text-xs font-mono font-semibold text-[#B91C1C] truncate">
            {auftrag.akten_nummer ?? auftrag.id.slice(0, 8)}
          </span>
          {mech && <MechanikerAvatar mech={mech} />}
        </div>

        <p className="text-sm font-medium text-gray-900 truncate leading-tight mb-0.5">
          {auftrag.kunde_name ?? '—'}
        </p>

        {auftrag.kennzeichen && (
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Car className="w-3 h-3 shrink-0" />
            <span className="font-mono truncate">{auftrag.kennzeichen}</span>
          </div>
        )}

        {fertigDate && (
          <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
            <Calendar className="w-3 h-3 shrink-0" />
            <span>bis {fertigDate}</span>
          </div>
        )}
      </Link>
    </div>
  );
}

// ─── Kanban Column ────────────────────────────────────────────────────────────

interface KanbanColumnProps {
  status: WerkstattStatus;
  auftraege: WerkstattAuftrag[];
  mechaniker: Mechaniker[];
  isOver: boolean;
}

function KanbanColumn({ status, auftraege, mechaniker, isOver }: KanbanColumnProps) {
  const { setNodeRef } = useDroppable({ id: status });

  return (
    <div
      ref={setNodeRef}
      data-kanban-column={status}
      className={`flex flex-col min-w-[220px] w-[220px] shrink-0 rounded-xl border transition-colors
        ${isOver ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200' : STATUS_FARBE[status] ?? 'bg-gray-50 border-gray-200'}`}
    >
      {/* Column header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-inherit">
        <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_HEADER_FARBE[status] ?? 'bg-gray-400'}`} />
        <span className="text-xs font-semibold text-gray-700 truncate flex-1">
          {STATUS_LABEL[status]}
        </span>
        <span className="text-xs text-gray-400 shrink-0 tabular-nums">{auftraege.length}</span>
      </div>

      {/* Cards */}
      <div className="flex flex-col gap-2 p-2 min-h-[120px]">
        {auftraege.map((a) => (
          <KanbanCard key={a.id} auftrag={a} mechaniker={mechaniker} />
        ))}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function WerkstattAuftraegePage() {
  const { user, session } = useAuth();
  const { auftraege, setAuftraege, loading, error } = useAuftraege();
  const mechaniker = useMechaniker();
  const [selectedMechaniker, setSelectedMechaniker] = useState<Set<string>>(new Set());
  const [showAbgeschlossen, setShowAbgeschlossen] = useState(false);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [overColumnId, setOverColumnId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );

  // Filter by mechaniker selection (empty set = all)
  const filtered = useMemo(() => {
    if (selectedMechaniker.size === 0) return auftraege;
    return auftraege.filter((a) => a.mechaniker_id && selectedMechaniker.has(a.mechaniker_id));
  }, [auftraege, selectedMechaniker]);

  // Group by status
  const byStatus = useMemo<Record<string, WerkstattAuftrag[]>>(() => {
    const map: Record<string, WerkstattAuftrag[]> = {};
    for (const s of WERKSTATT_STATUS) map[s] = [];
    for (const a of filtered) {
      const s = a.werkstatt_status ?? 'angenommen';
      if (map[s]) map[s].push(a);
    }
    return map;
  }, [filtered]);

  const activeDragAuftrag = useMemo(
    () => (activeDragId ? auftraege.find((a) => a.id === activeDragId) : null),
    [activeDragId, auftraege],
  );

  const visibleSpalten = showAbgeschlossen
    ? [...AKTIVE_SPALTEN, ...ABGESCHLOSSEN_SPALTEN]
    : AKTIVE_SPALTEN;

  function handleDragStart(event: DragStartEvent) {
    setActiveDragId(event.active.id as string);
  }

  function handleDragOver(event: { over: { id: string } | null }) {
    setOverColumnId(event.over?.id ?? null);
  }

  async function handleDragEnd(event: DragEndEvent) {
    setActiveDragId(null);
    setOverColumnId(null);

    const { active, over } = event;
    if (!over) return;

    const akteId = active.id as string;
    const newStatus = over.id as WerkstattStatus;
    const auftrag = auftraege.find((a) => a.id === akteId);
    if (!auftrag) return;
    if (auftrag.werkstatt_status === newStatus) return;

    const from = auftrag.werkstatt_status ?? null;
    if (!isAllowedTransition(from, newStatus)) {
      toast.error(
        `Übergang nicht erlaubt: ${STATUS_LABEL[from as WerkstattStatus] ?? 'NULL'} → ${STATUS_LABEL[newStatus]}`,
        { description: 'Dieser Status-Wechsel ist in der Werkstatt-Status-Kette nicht zulässig.' },
      );
      return;
    }

    // Optimistic update
    setAuftraege((prev) =>
      prev.map((a) => (a.id === akteId ? { ...a, werkstatt_status: newStatus } : a)),
    );

    try {
      const res = await fetch('/api/werkstatt-kanban', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ action: 'setStatus', akteId, newStatus, userId: user?.id ?? null }),
      });
      const json = await res.json();
      if (!json.ok) {
        // Rollback
        setAuftraege((prev) =>
          prev.map((a) => (a.id === akteId ? { ...a, werkstatt_status: from } : a)),
        );
        toast.error(json.error ?? 'Status konnte nicht gespeichert werden');
      }
    } catch {
      setAuftraege((prev) =>
        prev.map((a) => (a.id === akteId ? { ...a, werkstatt_status: from } : a)),
      );
      toast.error('Netzwerkfehler beim Speichern des Status');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <p className="text-sm text-red-600 mb-2">Fehler beim Laden der Aufträge</p>
        <p className="text-xs text-gray-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 sm:px-6 py-3 border-b border-gray-100 bg-white shrink-0">
        <div className="w-8 h-8 bg-[#B91C1C] rounded-lg flex items-center justify-center text-white shrink-0">
          <Wrench className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-semibold text-gray-900">Werkstatt-Aufträge</h1>
          <p className="text-xs text-gray-500">{auftraege.length} Aufträge gesamt</p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <MechanikerFilter
            mechaniker={mechaniker}
            selected={selectedMechaniker}
            onChange={setSelectedMechaniker}
          />

          <button
            onClick={() => setShowAbgeschlossen((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-colors
              ${showAbgeschlossen
                ? 'bg-gray-800 text-white border-gray-800'
                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`}
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            Abgeschlossen
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAbgeschlossen ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* Kanban board */}
      <div className="flex-1 overflow-x-auto overflow-y-auto p-4">
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver as any}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-3 h-full min-h-[400px] pb-4">
            {visibleSpalten.map((status) => (
              <KanbanColumn
                key={status}
                status={status}
                auftraege={byStatus[status] ?? []}
                mechaniker={mechaniker}
                isOver={overColumnId === status}
              />
            ))}
          </div>

          <DragOverlay dropAnimation={null}>
            {activeDragAuftrag ? (
              <KanbanCard auftrag={activeDragAuftrag} mechaniker={mechaniker} isDragOverlay />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
}
